<script setup>
import { ref, watch, onBeforeUnmount } from "vue";
import SectionHeading from "../ui/SectionHeading.vue";
import BaseButton from "../ui/BaseButton.vue";
import IconCheck from "../icons/IconCheck.vue";
import IconCalendar from "../icons/IconCalendar.vue";
import IconBell from "../icons/IconBell.vue";
import { useLanding } from "../../composables/useLanding";
import { useStaggerReveal } from "../../composables/useStaggerReveal";
import { scrollToLeadForm } from "../../composables/useScrollTo";
import { prefersReducedMotion } from "../../composables/useScrollReveal";
import { useSpotlight } from "../../composables/useSpotlight";
import { useGsap } from "../../composables/useGsap";
import MagneticWrap from "../ui/MagneticWrap.vue";

const steps = ref(null);
const { revealed } = useStaggerReveal(steps);

const content = useLanding().howItWorks;

useSpotlight(steps);

/*
 * Desktop: a rail above the three cards fills as the section scrolls past,
 * and each step's node lights when the fill reaches it. Scrubbed, so it
 * follows the scroll both ways.
 */
const section = ref(null);
const lit = ref(prefersReducedMotion() ? 3 : 0);

useGsap(section, (gsap) => {
  gsap.fromTo(
    ".rail__fill",
    { scaleX: 0 },
    {
      scaleX: 1,
      ease: "none",
      scrollTrigger: {
        trigger: ".steps",
        start: "top 80%",
        end: "bottom 55%",
        scrub: 0.5,
        onUpdate: (self) => {
          // The nodes sit at the start, middle and end of the track.
          lit.value = [0.02, 0.5, 0.98].filter((at) => self.progress >= at).length;
        },
      },
    }
  );
});

// Sample content for the step illustrations — drawings, not claims.
const art = content.art;

/*
 * The three drawings play in order once the steps scroll in: the name types
 * itself into the form and the form is sent, then the setup items tick, then
 * the notifications arrive. The ticks and notifications are CSS delays; only
 * the typing needs a timer.
 */
const typed = ref(prefersReducedMotion() ? art.formName.length : 0);
const sent = ref(false);
let timer = null;

watch(revealed, (isRevealed) => {
  if (!isRevealed || prefersReducedMotion()) {
    sent.value = isRevealed;
    return;
  }
  setTimeout(() => {
    timer = setInterval(() => {
      typed.value += 1;
      if (typed.value >= art.formName.length) {
        clearInterval(timer);
        timer = setTimeout(() => (sent.value = true), 350);
      }
    }, 180);
  }, 500);
});

onBeforeUnmount(() => {
  clearInterval(timer);
  clearTimeout(timer);
});
</script>

<template>
  <section ref="section" class="section section--surface" id="how-it-works">
    <div class="container">
      <SectionHeading
        :eyebrow="content.eyebrow"
        :title="content.title"
        :subtitle="content.subtitle"
      />

      <div class="rail" aria-hidden="true">
        <span class="rail__track"><span class="rail__fill" /></span>
        <span
          v-for="n in content.steps.length"
          :key="n"
          class="rail__node"
          :class="{ 'is-lit': n <= lit }"
        >{{ String(n).padStart(2, "0") }}</span>
      </div>

      <ol ref="steps" class="steps" role="list">
        <li
          v-for="(step, i) in content.steps"
          :key="step.step"
          class="step reveal spotlight"
          :class="[
            `step--${step.visual}`,
            { 'is-revealed': revealed, 'spotlight--on-ink': step.visual === 'booked' },
          ]"
          :style="{ '--reveal-index': i }"
        >
          <!-- Illustrations are decoration; the copy below carries the meaning. -->
          <div class="step__art" aria-hidden="true">
            <div v-if="step.visual === 'form'" class="art-form">
              <span class="art-field">
                <span class="art-field__label">שם מלא</span>
                <span class="art-field__value">
                  {{ art.formName.slice(0, typed) }}<i
                    v-if="!sent"
                    class="art-caret"
                  />
                </span>
              </span>
              <span class="art-field">
                <span class="art-field__label">טלפון</span>
                <span class="art-field__value" dir="ltr">050-•••-••67</span>
              </span>
              <span class="art-submit" :class="{ 'is-sent': sent }">
                <IconCheck v-if="sent" />
                {{ sent ? "נשלח" : "שלחו פרטים" }}
              </span>
            </div>

            <ul v-else-if="step.visual === 'setup'" class="art-setup">
              <li
                v-for="(item, n) in art.setupItems"
                :key="item"
                class="art-setup__item"
                :style="{ '--item-index': n }"
              >
                <span class="art-setup__check"><IconCheck /></span>
                {{ item }}
              </li>
            </ul>

            <div v-else-if="step.visual === 'booked'" class="art-stack">
              <div class="art-booked art-booked--behind">
                <span class="art-booked__icon art-booked__icon--bell"><IconBell /></span>
                <span class="art-booked__body">
                  <strong>{{ art.reminder.title }}</strong>
                  <span>{{ art.reminder.text }}</span>
                </span>
              </div>
              <div class="art-booked">
                <span class="art-booked__icon"><IconCalendar /></span>
                <span class="art-booked__body">
                  <strong>{{ art.booked.title }}</strong>
                  <span>{{ art.booked.text }}</span>
                </span>
                <span class="art-booked__badge"><IconCheck /></span>
              </div>
            </div>
          </div>

          <div class="step__body">
            <span class="step__number">שלב {{ step.step }}</span>
            <h3 class="step__title">{{ step.title }}</h3>
            <p class="step__text">{{ step.text }}</p>
          </div>
        </li>
      </ol>

      <div class="steps__cta">
        <MagneticWrap>
          <BaseButton size="lg" @click="scrollToLeadForm('how_it_works')">
            מתחילים
          </BaseButton>
        </MagneticWrap>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* ------------------------------------------------------------------ rail */

/*
 * Three numbered nodes over the three columns, joined by a track that fills
 * with the scroll. Desktop only: on phones the steps stack and the cards'
 * own labels carry the order.
 */
.rail {
  display: none;
}

@media (min-width: 800px) {
  .rail {
    position: relative;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-5);
    margin-block-end: var(--space-5);
  }

  /* From the first column's centre to the last one's. */
  .rail__track {
    position: absolute;
    inset-block-start: 50%;
    inset-inline: calc((100% - 2 * var(--space-5)) / 6);
    block-size: 2px;
    margin-block-start: -1px;
    border-radius: var(--radius-pill);
    background: var(--line);
    overflow: hidden;
  }

  .rail__fill {
    display: block;
    block-size: 100%;
    background: linear-gradient(to left, var(--brand), var(--accent));
    transform-origin: right;
  }

  .rail__node {
    position: relative;
    justify-self: center;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    inline-size: 40px;
    block-size: 40px;
    border-radius: 50%;
    border: 2px solid var(--line-strong);
    background: var(--surface);
    font-size: var(--text-sm);
    font-weight: var(--weight-bold);
    font-variant-numeric: tabular-nums;
    color: var(--ink-subtle);
    transition: background-color var(--dur-base) var(--ease-out),
      border-color var(--dur-base) var(--ease-out),
      color var(--dur-base) var(--ease-out),
      transform var(--dur-slow) var(--ease-spring);
  }

  .rail__node.is-lit {
    border-color: var(--brand);
    background: var(--brand);
    color: var(--on-brand);
    transform: scale(1.12);
  }
}

.steps {
  display: grid;
  gap: var(--space-4);
  padding: 0;
  margin: 0;
  list-style: none;
}

@media (min-width: 800px) {
  .steps {
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-5);
  }
}

/* ------------------------------------------------------------------ step */

.step {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: var(--border);
  border-radius: var(--radius-2xl);
  background: var(--canvas);
  transition: transform var(--dur-base) var(--ease-out),
    box-shadow var(--dur-base) var(--ease-out);
}

/* The last step is the payoff, so it gets the dark treatment. */
.step--booked {
  background: var(--ink);
  border-color: transparent;
}

.step__art {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-block-size: 230px;
  padding: var(--space-6) var(--space-6) var(--space-4);
}

.step__body {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: 0 var(--space-6) var(--space-6);
}

.step__number {
  align-self: flex-start;
  padding: 2px var(--space-3);
  border-radius: var(--radius-pill);
  background: var(--brand-soft);
  color: var(--brand-ink);
  font-size: var(--text-xs);
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
}

.step__title {
  font-size: var(--text-xl);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-tight);
}

.step__text {
  margin: 0;
  color: var(--ink-muted);
}

.step--booked .step__number {
  background: rgba(255, 255, 255, 0.12);
  color: var(--on-ink);
}

.step--booked .step__title {
  color: var(--on-ink);
}

.step--booked .step__text {
  color: var(--on-ink-muted);
}

/* ------------------------------------------------------------ art: form */

.art-form {
  display: grid;
  gap: var(--space-2);
  inline-size: min(100%, 260px);
  padding: var(--space-4);
  border: var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--lift-2);
  transition: transform var(--dur-base) var(--ease-spring);
}

.step:hover .art-form {
  transform: rotate(-2deg);
}

.art-field {
  display: grid;
  gap: 2px;
}

.art-field__label {
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  color: var(--ink-subtle);
}

.art-field__value {
  padding: var(--space-1) var(--space-3);
  border: var(--border-strong);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
  color: var(--ink);
  text-align: start;
}

.art-caret {
  display: inline-block;
  inline-size: 1.5px;
  block-size: 1em;
  margin-inline-start: 1px;
  vertical-align: text-bottom;
  background: var(--brand);
  animation: blink 900ms steps(2, start) infinite;
}

.art-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
  transition: background-color var(--dur-base) var(--ease-out);
  margin-block-start: var(--space-1);
  padding-block: var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--brand);
  color: var(--on-brand);
  font-size: var(--text-sm);
  font-weight: var(--weight-bold);
  text-align: center;
}

.art-submit.is-sent {
  background: var(--success);
  animation: press 450ms var(--ease-spring);
}

.art-submit :deep(svg) {
  inline-size: 14px;
  block-size: 14px;
  stroke-width: 3;
}

@keyframes press {
  30% {
    transform: scale(0.94);
  }
}

/* ----------------------------------------------------------- art: setup */

.art-setup {
  display: grid;
  gap: var(--space-2);
  inline-size: min(100%, 260px);
  margin: 0;
  padding: 0;
  list-style: none;
}

.art-setup__item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border: var(--border);
  border-radius: var(--radius-md);
  background: var(--surface);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--ink);
}

.art-setup__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: 22px;
  block-size: 22px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--success);
  color: var(--on-brand);
  transform: scale(0);
  transition: transform var(--dur-slow) var(--ease-spring);
  /* After the form in step 01 is sent. */
  transition-delay: calc(1700ms + var(--item-index) * 260ms);
}

.is-revealed .art-setup__check,
html:not(.js) .art-setup__check {
  transform: scale(1);
}

.art-setup__check :deep(svg) {
  inline-size: 13px;
  block-size: 13px;
  stroke-width: 3;
}

/* ---------------------------------------------------------- art: booked */

/* Two notifications, the reminder tucked behind the confirmation. */
.art-stack {
  display: grid;
  justify-items: center;
  inline-size: min(100%, 280px);
  margin-block-start: var(--space-8);
}

.art-stack > * {
  grid-area: 1 / 1;
}

/* After the last setup item ticks: the confirmation, then the reminder. */
.art-booked {
  opacity: 0;
  translate: 0 24px;
  transition: opacity var(--dur-slow) var(--ease-out),
    translate var(--dur-slow) var(--ease-spring),
    transform var(--dur-base) var(--ease-spring);
  transition-delay: 2900ms, 2900ms, 0ms;
}

.art-booked--behind {
  transform: translate3d(0, -62px, 0) scale(0.92);
  transition-delay: 3500ms, 3500ms, 0ms;
}

.is-revealed .art-booked,
html:not(.js) .art-booked {
  opacity: 1;
  translate: 0 0;
}

.is-revealed .art-booked--behind,
html:not(.js) .art-booked--behind {
  opacity: 0.8;
}

.step:hover .art-booked--behind {
  transform: translate3d(0, -70px, 0) scale(0.92);
}

.art-booked__icon--bell {
  background: var(--accent-soft);
  color: var(--accent);
}

.art-booked {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  inline-size: min(100%, 280px);
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--lift-3);
}

.step:hover .art-booked:not(.art-booked--behind) {
  transform: translate3d(0, 6px, 0);
}

.art-booked__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  inline-size: 40px;
  block-size: 40px;
  border-radius: var(--radius-md);
  background: var(--brand-soft);
  color: var(--brand);
}

.art-booked__icon :deep(svg) {
  inline-size: 20px;
  block-size: 20px;
}

.art-booked__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-inline-size: 0;
  font-size: var(--text-sm);
  line-height: var(--leading-snug);
}

.art-booked__body strong {
  color: var(--ink);
}

.art-booked__body span {
  font-size: var(--text-xs);
  color: var(--ink-subtle);
}

.art-booked__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  inline-size: 26px;
  block-size: 26px;
  border-radius: 50%;
  background: var(--success-soft);
  color: var(--success);
}

.art-booked__badge :deep(svg) {
  inline-size: 14px;
  block-size: 14px;
  stroke-width: 3;
}

/* ------------------------------------------------------------------- cta */

.steps__cta {
  display: flex;
  justify-content: center;
  margin-block-start: var(--space-8);
}

@media (prefers-reduced-motion: reduce) {
  .art-setup__check,
  .art-booked {
    transition-delay: 0ms;
  }
}

@media (max-width: 799px) {
  .step__art {
    min-block-size: 0;
    padding: var(--space-6) var(--space-5) var(--space-4);
  }


  .step__body {
    padding: 0 var(--space-5) var(--space-5);
  }
}
</style>
